// src/components/VideoEngine/Common/AudioPreview.js

import React, { useState, useEffect, useRef } from 'react';
import { ActionIcon, RingProgress, Group } from '@mantine/core';
import { IconPlayerPlay, IconPlayerPause } from '@tabler/icons-react';

export const AudioPreview = ({ src, id, activeId, onToggle }) => {
    // Derived State: Am I the one currently allowed to play?
    const isPlaying = activeId === id;
    
    const [progress, setProgress] = useState(0);
    const audioRef = useRef(null);

    // 1. Initialize Audio Object (Run once per source)
    useEffect(() => {
        const audio = new Audio(src);
        audioRef.current = audio;

        // Event: Update Progress Ring
        const updateProgress = () => {
            if (audio.duration) {
                setProgress((audio.currentTime / audio.duration) * 100);
            }
        };

        // Event: Track Finished
        const handleEnded = () => {
            setProgress(0);
            // Tell parent to reset state (no one playing)
            if (onToggle) onToggle(null); 
        };

        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('ended', handleEnded);

        // Cleanup
        return () => {
            audio.pause();
            audio.removeEventListener('timeupdate', updateProgress);
            audio.removeEventListener('ended', handleEnded);
            audio.src = ''; 
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]); 

    // 2. Sync Playback with Parent State (The "Remote Control" Logic)
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            // Parent says play -> Attempt play
            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.warn("Playback prevented:", error);
                    // If blocked, tell parent to turn us off
                    if (onToggle) onToggle(null);
                });
            }
        } else {
            // Parent says stop (or another track took over) -> Pause
            audio.pause();
        }
    }, [isPlaying, onToggle]);

    // 3. Handle UI Click
    const handleClick = () => {
        if (isPlaying) {
            onToggle(null); // Turn me off
        } else {
            onToggle(id); // Turn me on (and implicitly others off)
        }
    };

    return (
        <Group spacing="xs" align="center">
            <div style={{ position: 'relative', width: 40, height: 40 }}>
                <RingProgress
                    size={40}
                    thickness={3}
                    roundCaps
                    sections={[{ value: progress, color: 'green' }]}
                    style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
                />
                <ActionIcon 
                    variant="transparent" 
                    radius="xl" 
                    size="md" 
                    onClick={handleClick}
                    style={{ position: 'absolute', top: 5, left: 5, zIndex: 2 }}
                    color="gray"
                >
                    {isPlaying ? <IconPlayerPause size={18} fill="currentColor" /> : <IconPlayerPlay size={18} fill="currentColor" />}
                </ActionIcon>
            </div>
        </Group>
    );
};